'use client';

import { useState, useRef, useCallback, type RefObject } from 'react';
import {
  RealtimeAgent,
  RealtimeSession,
  OpenAIRealtimeWebRTC,
  type RealtimeSessionConfig,
} from '@openai/agents/realtime';
import { tool } from '@openai/agents';
import { z } from 'zod';
import type { OrbLiveLevels, OrbState } from '@/components/interview/Orb';
import { isNonProdHostname } from '@/lib/environment';
import { buildRealtimeSystemPrompt } from '@/lib/promptBuilder';
import {
  getActiveMockStudyScenario,
  type MockStudyScenario,
} from '@/lib/mockStudyScenarios';
import {
  SHORT_UTTERANCE_MS,
  TURN_GATE_TIMEOUT_PAD_MS,
  hasUsableTranscriptContent,
} from '@/lib/turnGate';
import type { InterviewPhase } from '@/lib/interviewPhase';

export type { InterviewPhase } from '@/lib/interviewPhase';

/** AND-barrier state for the latest speech_stopped → response.create decision. */
type PendingTurnBarriers = {
  needsGate: boolean;
  graceDone: boolean;
  gateResolved: boolean;
  gatePassed: boolean;
  placeholderId: string | null;
  durationMs: number;
};

// ── Types ─────────────────────────────────────────────────────────────────────

export type ConnectionStatus = 'idle' | 'connecting' | 'ready' | 'error';

export interface TranscriptEntry {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** opening → interviewing → closing at the time this turn was recorded. */
  phase?: InterviewPhase;
  /**
   * Primary event time persisted to recordings.
   * For user speech this is when speech started; for assistant turns it is when
   * transcript finalisation arrives.
   */
  timestamp?: string;
  /** ISO timestamp when user speech started (if available). */
  spokenAt?: string;
  /** ISO timestamp when transcription completed (if available). */
  transcribedAt?: string;
  /** Relative path to per-turn user audio (e.g. user-turns/item_xxx.webm). */
  audioFile?: string;
}

export interface UseRealtimeSessionResult {
  status: ConnectionStatus;
  orbState: OrbState;
  /**
   * Mutable mic/AI levels updated every animation frame without React state.
   * Pass to `<Orb liveLevelsRef={...} />` so the orb can animate without re-renders.
   */
  liveLevelsRef: RefObject<OrbLiveLevels>;
  /** True once the AI has called the end_interview tool. */
  ended: boolean;
  /** opening → interviewing (first user speech) → closing (end_interview detected). */
  phase: InterviewPhase;
  error: string | null;
  transcript: TranscriptEntry[];
  /** Streams in as audio_transcript.delta events arrive. */
  currentAiText: string;
  /** Live streaming text during AI speech — clears when the turn ends. For debug/test UIs. */
  streamingAiText: string;
  /** Changes each AI turn to trigger fade transitions. */
  aiTurnId: string;
  start: (
    voice?: string,
    existingStream?: MediaStream,
    noiseReductionType?: 'near_field' | 'far_field',
    outputDeviceId?: string,
  ) => Promise<void>;
  stop: () => void;
  /** Send a text message (test interface). */
  sendText: (text: string) => void;
  /** Ask the AI to repeat its last question. */
  repeat: () => void;
  /** Enable or disable the outgoing mic track. */
  mute: (shouldMute: boolean) => void;
  /** Pause/resume server-side VAD. */
  pause: (shouldPause: boolean) => void;
}

export type UseRealtimeSessionOptions = {
  /** Registry study id (e.g. AI_companionship_m4p9). Omit for demo sessions. */
  studyId?: string;
  config?: MockStudyScenario;
  /** Qualtrics ResponseID passed via query string when embedded. */
  responseId?: string;
  /** Marks session as internal test data (e.g. URL ?test=1). */
  isTest?: boolean;
  /** Fires once when the interview completes naturally (after closing audio). */
  onCompleted?: (payload: { sessionId: string }) => void;
};

// ── VAD configs ───────────────────────────────────────────────────────────────

// create_response is intentionally false: with interrupt_response:false the server
// cannot cancel an ongoing AI response, so if it also auto-creates on speech_stop it
// will collide and return "active response in progress". We send response.create
// manually from speech_stopped when the AI is not currently responding.
const OPENING_VAD_CONFIG = {
  type: 'semantic_vad',
  interrupt_response: false,
  create_response: false,
  eagerness: 'high',
} as const;

const INTERVIEWING_VAD_CONFIG = {
  type: 'semantic_vad',
  interrupt_response: false,
  create_response: false,
  eagerness: 'medium',
} as const;

const PROD_TRANSCRIPTION_MODEL = 'gpt-4o-transcribe';
const NON_PROD_TRANSCRIPTION_MODEL = 'gpt-4o-mini-transcribe';

function getTranscriptionModelFromHostname(hostname: string): string {
  return isNonProdHostname(hostname)
    ? NON_PROD_TRANSCRIPTION_MODEL
    : PROD_TRANSCRIPTION_MODEL;
}

// Slow-paced playback estimate for hard-timeout safety net only (when buffer.stopped
// is missing/late). Intentionally slower than typical TTS so hard rarely races ahead
// of a late-but-valid stopped event.
const HARD_FALLBACK_MS_PER_CHAR = 70;
const HARD_FALLBACK_PAD_MS = 4_000;
// Ignore "instant" started→stopped bursts (seen when response events arrive batched
// before client playback). Fall back to estimate-based confirm instead.
const PREMATURE_BUFFER_STOPPED_MS = 800;
// Remaining hard-timeout bounds after response.done / transcript.done.
const HARD_AI_DONE_MIN_MS = 10_000;
const HARD_AI_DONE_MAX_MS = 60_000;
// Warn-only: fires after the hard fallback deadline if confirmAiDone still
// has not run. Never calls confirm — diagnostics only.
const STUCK_THINKING_WATCHDOG_PAD_MS = 5_000;
// Tool-only / no-audio responses never emit buffer.stopped — confirm quickly.
const NO_AUDIO_RESPONSE_DONE_MS = 400;
// If response.create is accepted locally but response.created is slow, recover
// mic/UI. Do NOT auto-retry create — a second create while the first is
// queued/active causes "active response in progress" and can mix turns.
const RESPONSE_CREATE_STALL_MS = 5_000;

// Grace period after speech_stopped before sending response.create.
const RESPONSE_GRACE_MS = 2000;

// Diagnostics for Qualtrics iframe / Realtime turn-taking.
const WATCHED_REALTIME_EVENTS = new Set([
  'input_audio_buffer.speech_started',
  'input_audio_buffer.speech_stopped',
  'input_audio_buffer.committed',
  'conversation.item.deleted',
  'response.created',
  'response.output_audio.done',
  'response.done',
  'output_audio_buffer.started',
  'output_audio_buffer.stopped',
  'output_audio_buffer.cleared',
  'session.updated',
  'error',
]);

// Closing protocol: model calls end_interview alone (no speech in that turn).
// SDK auto-executes the tool and triggers a follow-up response for the closing
// message. If the model already spoke closing in the same turn (C3), we cancel
// the follow-up to avoid duplicate audio.
function createInterviewAgent(config: MockStudyScenario): RealtimeAgent {
  const endInterviewTool = tool({
    name: 'end_interview',
    description:
      'Signal that the interview is ready to conclude. Call this alone in its own ' +
      'turn — do NOT speak the closing message in the same response. The closing ' +
      'message will be delivered automatically after this tool executes.',
    parameters: z.object({}),
    needsApproval: false,
    execute: async () => {
      const closingText = config.closingMessage;
      return JSON.stringify({
        status: 'end_acknowledged',
        instruction:
          'Speak the following closing message aloud verbatim. Do not call end_interview again.',
        closing_message: closingText,
      });
    },
  });

  return new RealtimeAgent({
    name: 'AI Interviewer',
    instructions: buildRealtimeSystemPrompt(config),
    tools: [endInterviewTool],
  });
}

// ── Recording helpers (module-level) ──────────────────────────────────────────

function getRecordingMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  return MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
    ? 'audio/webm;codecs=opus'
    : '';
}

// Streams audio chunks out every 10 s so data is persisted as the interview
// progresses (rather than only at the end).
function createStreamingRecorder(
  stream: MediaStream,
  onChunk: (blob: Blob) => void,
): MediaRecorder {
  const mime = getRecordingMime();
  const recorder = mime
    ? new MediaRecorder(stream, { mimeType: mime })
    : new MediaRecorder(stream);
  recorder.ondataavailable = (e) => { if (e.data.size > 0) onChunk(e.data); };
  recorder.start(10000);
  return recorder;
}

/** One-shot recorder for a single user speech turn (speech_started → speech_stopped). */
function createTurnRecorder(
  stream: MediaStream,
  onComplete: (blob: Blob) => void,
  shouldDiscard: () => boolean,
): MediaRecorder {
  const chunks: Blob[] = [];
  const mime = getRecordingMime();
  const recorder = mime
    ? new MediaRecorder(stream, { mimeType: mime })
    : new MediaRecorder(stream);
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
  recorder.onstop = () => {
    if (shouldDiscard() || chunks.length === 0) return;
    onComplete(new Blob(chunks, { type: mime || 'audio/webm' }));
  };
  recorder.start();
  return recorder;
}

function stopRecorder(recorder: MediaRecorder | null) {
  if (recorder && recorder.state !== 'inactive') recorder.stop();
}

function pauseRecorder(recorder: MediaRecorder | null) {
  if (!recorder || recorder.state !== 'recording') return;
  if (typeof recorder.pause === 'function') recorder.pause();
}

function resumeRecorder(recorder: MediaRecorder | null) {
  if (!recorder || recorder.state !== 'paused') return;
  if (typeof recorder.resume === 'function') recorder.resume();
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useRealtimeSession(options?: UseRealtimeSessionOptions): UseRealtimeSessionResult {
  const isDemoSession = !options?.studyId;
  const interviewConfig = options?.config ?? getActiveMockStudyScenario();
  const recordingStudyId = options?.studyId;
  const recordingResponseId = options?.responseId;
  const recordingIsTest = options?.isTest === true;
  const onCompletedRef = useRef(options?.onCompleted);
  onCompletedRef.current = options?.onCompleted;
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [orbState, setOrbState] = useState<OrbState>('idle');
  const liveLevelsRef = useRef<OrbLiveLevels>({ mic: 0, ai: 0 });
  const [ended, setEnded] = useState(false);
  const [phase, setPhase] = useState<InterviewPhase>('opening');
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [currentAiText, setCurrentAiText] = useState('');
  const [streamingAiText, setStreamingAiText] = useState('');
  const [aiTurnId, setAiTurnId] = useState('');

  const sessionRef = useRef<RealtimeSession | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const micCtxRef = useRef<AudioContext | null>(null);
  const mouseDownHandlerRef = useRef<(() => void) | null>(null);
  const rafRef = useRef<number>(0);
  const remoteAnalyserRef = useRef<AnalyserNode | null>(null);
  const remoteAudioCtxRef = useRef<AudioContext | null>(null);
  const aiLevelRafRef = useRef<number>(0);
  const partialRef = useRef<Record<string, string>>({});
  const userMutedRef = useRef(false);
  const userPausedRef = useRef(false);
  const suppressUserRecordingRef = useRef(false);
  const micRenableTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True from response.create/created until confirmAiDone — guards VAD while AI speaks.
  const isAiRespondingRef = useRef(false);
  // True only while the API response is still active (response.created →
  // response.done/cancelled). Safe to send response.cancel only when true.
  const isResponseActiveRef = useRef(false);
  const speechTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gateTimeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseRef = useRef<InterviewPhase>('opening');
  // Placeholder IDs inserted on speech_started, in start order.
  const pendingUserPlaceholderQueueRef = useRef<string[]>([]);
  // Maps a committed user item_id to its placeholder ID so replacement is stable
  // even when multiple user turns are in flight.
  const pendingUserPlaceholderByItemIdRef = useRef<Record<string, string>>({});
  // speech_started timestamps in start order, used when no placeholder exists.
  const pendingUserSpeechStartTimesRef = useRef<string[]>([]);
  // AI transcript entries accumulated during a response turn. Flushed to state
  // only after the AI turn is confirmed done so text appears after speech, not during.
  const pendingAiEntriesRef = useRef<TranscriptEntry[]>([]);
  // Total characters of the current AI response's transcript, accumulated as deltas
  // stream in. Used for estimated-playback hard timeout when buffer.stopped misses.
  const aiResponseCharsRef = useRef(0);
  // Opening should only produce one assistant item; any later assistant leakage
  // before the user replies is dropped.
  const openingAssistantItemSeenRef = useRef(false);
  // Tracks whether the user is currently mid-utterance (speech_started without a
  // matching speech_stopped), regardless of phase.
  const userSpeakingRef = useRef(false);
  // performance.now() + placeholder for the in-flight utterance (speech_started).
  const utteranceStartedAtMsRef = useRef(0);
  const utterancePlaceholderIdRef = useRef<string | null>(null);
  // Interviewing: grace + optional short-utterance transcript gate (AND barrier).
  const pendingTurnRef = useRef<PendingTurnBarriers | null>(null);
  // Turns dropped by the gate — ignore late transcription fallbacks that would re-insert them.
  const discardedPlaceholderIdsRef = useRef<Set<string>>(new Set());
  const discardedUserItemIdsRef = useRef<Set<string>>(new Set());
  // Deduplicate immediate server deletes for the same item id.
  const deletedServerItemIdsRef = useRef<Set<string>>(new Set());

  // True when the in-flight AI response began during the opening phase. The opening
  // message must never appear as a caption — but by the time it's flushed the phase
  // has often already flipped to 'interviewing' (the user's reply transitioned it),
  // so we can't rely on phase at flush time. Captured at response.created instead.
  const currentResponseIsOpeningRef = useRef(true);
  // Set to true by the FIRST confirmAiDone for each response turn; cleared by
  // response.created. Guards against duplicate confirmAiDone.
  const aiTurnConfirmedDoneRef = useRef(false);
  // Recording — audio is streamed to /api/save-recording, transcript persisted alongside.
  const sessionIdRef = useRef<string | null>(null);

  const appendRecordingFields = useCallback((fd: FormData) => {
    if (!sessionIdRef.current) return;
    fd.append('sessionId', sessionIdRef.current);
    if (recordingStudyId) fd.append('studyId', recordingStudyId);
    if (recordingResponseId) fd.append('responseId', recordingResponseId);
    if (recordingIsTest) fd.append('isTest', '1');
  }, [recordingStudyId, recordingResponseId, recordingIsTest]);
  const mixedRecorderRef = useRef<MediaRecorder | null>(null);
  const userTurnRecorderRef = useRef<MediaRecorder | null>(null);
  const mixCtxRef = useRef<AudioContext | null>(null);
  const mixDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const remoteMixConnectedRef = useRef(false);
  const userTurnPendingItemIdRef = useRef<string | null>(null);
  const userTurnPendingBlobRef = useRef<Blob | null>(null);
  const userTurnDiscardRef = useRef(false);
  const pendingAudioFileByItemIdRef = useRef<Record<string, string>>({});
  // Mirror of transcript state — updated in sync with every setTranscript call so
  // persistTranscript() can read the latest value without waiting for a re-render.
  const transcriptRef = useRef<TranscriptEntry[]>([]);
  const interviewFinalizedRef = useRef(false);
  // Finalize after follow-up closing speech finishes playing (buffer.stopped).
  const deferFinalizeRef = useRef(false);
  // True once the current response streams any assistant audio transcript.
  const responseHasAudioRef = useRef(false);
  // True once the output analyser detects closing speech playing on the client.
  const closingPlaybackSeenRef = useRef(false);
  // True after tool-only end_interview until follow-up closing audio streams.
  const awaitingClosingFollowUpRef = useRef(false);
  // True when closing audio was already in the end_interview turn — cancel SDK follow-up.
  const suppressClosingFollowUpRef = useRef(false);
  const currentResponseIdRef = useRef<string | null>(null);
  // Hard wall-clock fallback if buffer.stopped never arrives after response.done.
  const aiDoneHardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Warn-only timer armed alongside hard fallback — logs if confirm never arrives.
  const stuckThinkingWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // response.done seen for an audio turn but transcript length not ready yet —
  // arm the slow hard estimate once transcript.done arrives.
  const awaitingHardAfterTranscriptRef = useRef(false);
  // When the current AI response was created (performance.now) — for playback estimates.
  const aiResponseStartedAtRef = useRef(0);
  // When output_audio_buffer.started fired for the current response.
  const outputBufferStartedAtRef = useRef(0);
  // When a trustworthy output_audio_buffer.stopped fired (performance.now).
  const outputBufferStoppedAtRef = useRef(0);
  // True if this turn saw a premature started→stopped burst that was ignored.
  const prematureBufferStoppedRef = useRef(false);
  // Watchdog: response.create requested locally but response.created not yet seen.
  const pendingCreateSourceRef = useRef<string | null>(null);
  const pendingCreateAtRef = useRef(0);
  const createStallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Interviewing VAD applied once after first post-opening response.created (not mid-create).
  const interviewingVadAppliedRef = useRef(false);

  // ── Mic helpers ─────────────────────────────────────────────────────────────

  const setMicTrackEnabled = useCallback((enabled: boolean) => {
    micStreamRef.current
      ?.getTracks()
      .filter((t) => t.kind === 'audio')
      .forEach((t) => { t.enabled = enabled; });
  }, []);

  const applyMicOpen = useCallback((wantOpen: boolean) => {
    setMicTrackEnabled(wantOpen && !userMutedRef.current && !userPausedRef.current);
  }, [setMicTrackEnabled]);

  const clearSpeechResponseTimer = useCallback(() => {
    if (speechTimerRef.current) {
      clearTimeout(speechTimerRef.current);
      speechTimerRef.current = null;
    }
  }, []);

  const clearGateTimeoutTimer = useCallback(() => {
    if (gateTimeoutTimerRef.current) {
      clearTimeout(gateTimeoutTimerRef.current);
      gateTimeoutTimerRef.current = null;
    }
  }, []);

  const clearPendingTurnBarriers = useCallback(() => {
    clearSpeechResponseTimer();
    clearGateTimeoutTimer();
    pendingTurnRef.current = null;
  }, [clearGateTimeoutTimer, clearSpeechResponseTimer]);

  const removeUserTranscriptEntry = useCallback((entryId: string | null) => {
    if (!entryId) return;
    const cur = transcriptRef.current;
    const idx = cur.findIndex((e) => e.id === entryId && e.role === 'user');
    if (idx < 0) return;
    const next = [...cur];
    next.splice(idx, 1);
    transcriptRef.current = next;
    setTranscript(next);
  }, []);

  /** Remove a discarded noise/filler turn from the Realtime conversation history. */
  const deleteServerConversationItem = useCallback((itemId: string) => {
    if (deletedServerItemIdsRef.current.has(itemId)) return;
    deletedServerItemIdsRef.current.add(itemId);

    const session = sessionRef.current;
    if (!session) return;
    try {
      const inHistory = session.history.some((item) => item.itemId === itemId);
      if (inHistory) {
        // Keeps Agents SDK local history in sync; resetHistory emits delete.
        session.updateHistory((history) => history.filter((item) => item.itemId !== itemId));
        console.log(`[turn-gate] conversation.item.delete item=${itemId} via=updateHistory`);
      } else {
        // Item may not be in SDK history yet — purge on the server directly.
        session.transport.sendEvent({
          type: 'conversation.item.delete',
          item_id: itemId,
        });
        console.log(`[turn-gate] conversation.item.delete item=${itemId} via=transport`);
      }
    } catch (err) {
      console.log(`[turn-gate] conversation.item.delete failed item=${itemId}`, err);
    }
  }, []);

  const stopResources = useCallback(() => {
    if (mouseDownHandlerRef.current) {
      document.removeEventListener('mousedown', mouseDownHandlerRef.current);
      mouseDownHandlerRef.current = null;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (aiLevelRafRef.current) { cancelAnimationFrame(aiLevelRafRef.current); aiLevelRafRef.current = 0; }
    if (micRenableTimerRef.current) {
      clearTimeout(micRenableTimerRef.current);
      micRenableTimerRef.current = null;
    }
    if (speechTimerRef.current) {
      clearTimeout(speechTimerRef.current);
      speechTimerRef.current = null;
    }
    if (gateTimeoutTimerRef.current) {
      clearTimeout(gateTimeoutTimerRef.current);
      gateTimeoutTimerRef.current = null;
    }
    pendingTurnRef.current = null;
    utteranceStartedAtMsRef.current = 0;
    utterancePlaceholderIdRef.current = null;
    discardedPlaceholderIdsRef.current.clear();
    discardedUserItemIdsRef.current.clear();
    deletedServerItemIdsRef.current.clear();
    if (aiDoneHardTimerRef.current) {
      clearTimeout(aiDoneHardTimerRef.current);
      aiDoneHardTimerRef.current = null;
    }
    if (stuckThinkingWatchdogRef.current) {
      clearTimeout(stuckThinkingWatchdogRef.current);
      stuckThinkingWatchdogRef.current = null;
    }
    if (createStallTimerRef.current) {
      clearTimeout(createStallTimerRef.current);
      createStallTimerRef.current = null;
    }
    pendingCreateSourceRef.current = null;
    pendingCreateAtRef.current = 0;
    interviewingVadAppliedRef.current = false;
    awaitingHardAfterTranscriptRef.current = false;
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    micCtxRef.current?.close().catch(() => {});
    micCtxRef.current = null;
    remoteAudioCtxRef.current?.close().catch(() => {});
    remoteAudioCtxRef.current = null;
    remoteAnalyserRef.current = null;
    userMutedRef.current = false;
    userPausedRef.current = false;
    suppressUserRecordingRef.current = false;
    mixCtxRef.current?.close().catch(() => {});
    mixCtxRef.current = null;
    mixDestinationRef.current = null;
    remoteMixConnectedRef.current = false;
    liveLevelsRef.current.mic = 0;
    liveLevelsRef.current.ai = 0;
  }, []);

  const persistTranscript = useCallback(() => {
    if (!sessionIdRef.current || !transcriptRef.current.length) return;
    const fd = new FormData();
    appendRecordingFields(fd);
    fd.append('transcript', JSON.stringify(transcriptRef.current));
    fetch('/api/save-recording', { method: 'POST', body: fd }).catch(console.error);
  }, [appendRecordingFields]);

  const sendMixedAudioChunk = useCallback((blob: Blob) => {
    if (!sessionIdRef.current || userPausedRef.current) return;
    const fd = new FormData();
    appendRecordingFields(fd);
    fd.append('mixedAudioChunk', blob, 'chunk.webm');
    fetch('/api/save-recording', { method: 'POST', body: fd }).catch(console.error);
  }, [appendRecordingFields]);

  const attachAudioFileToUserTurn = useCallback((itemId: string) => {
    const audioFile = `user-turns/${itemId}.webm`;
    const placeholderId = pendingUserPlaceholderByItemIdRef.current[itemId];
    const cur = transcriptRef.current;
    const idx = cur.findIndex(
      (e) => e.role === 'user' && (e.id === itemId || (placeholderId && e.id === placeholderId)),
    );
    if (idx < 0) {
      pendingAudioFileByItemIdRef.current[itemId] = audioFile;
      return;
    }
    if (cur[idx].audioFile === audioFile) return;
    const next = [...cur];
    next[idx] = { ...next[idx], audioFile };
    transcriptRef.current = next;
    setTranscript(next);
    persistTranscript();
  }, [persistTranscript]);

  const tryUploadUserTurn = useCallback(() => {
    const itemId = userTurnPendingItemIdRef.current;
    const blob = userTurnPendingBlobRef.current;
    if (!sessionIdRef.current || !itemId || !blob) return;

    userTurnPendingItemIdRef.current = null;
    userTurnPendingBlobRef.current = null;

    const fd = new FormData();
    appendRecordingFields(fd);
    fd.append('userTurnItemId', itemId);
    fd.append('userTurnAudio', blob, `${itemId}.webm`);
    fetch('/api/save-recording', { method: 'POST', body: fd })
      .then((r) => { if (r.ok) attachAudioFileToUserTurn(itemId); })
      .catch(console.error);
  }, [attachAudioFileToUserTurn, appendRecordingFields]);

  const cancelUserTurnCapture = useCallback(() => {
    const recorder = userTurnRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      userTurnDiscardRef.current = true;
      recorder.stop();
    }
    userTurnRecorderRef.current = null;
    userTurnPendingBlobRef.current = null;
    userTurnPendingItemIdRef.current = null;
  }, []);

  const stopUserTurnCapture = useCallback(() => {
    const recorder = userTurnRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.stop();
    userTurnRecorderRef.current = null;
  }, []);

  const startUserTurnCapture = useCallback(() => {
    if (suppressUserRecordingRef.current) return;
    const stream = micStreamRef.current;
    if (!stream || userTurnRecorderRef.current) return;
    userTurnDiscardRef.current = false;
    userTurnRecorderRef.current = createTurnRecorder(
      stream,
      (blob) => {
        userTurnPendingBlobRef.current = blob;
        tryUploadUserTurn();
      },
      () => userTurnDiscardRef.current,
    );
  }, [tryUploadUserTurn]);

  const updateRecordingCapture = useCallback(() => {
    const pauseMixed = userPausedRef.current;
    const suppressUser = userMutedRef.current || userPausedRef.current;
    suppressUserRecordingRef.current = suppressUser;
    if (suppressUser) cancelUserTurnCapture();
    if (pauseMixed) pauseRecorder(mixedRecorderRef.current);
    else resumeRecorder(mixedRecorderRef.current);
  }, [cancelUserTurnCapture]);

  const flushPendingAiEntries = useCallback(() => {
    const pending = pendingAiEntriesRef.current;
    if (pending.length === 0) return;
    // The opening message must never surface as a caption (the phase may already
    // have flipped to 'interviewing' by now). Still record it to the transcript
    // below for recordings — only the on-screen caption is suppressed.
    if (!currentResponseIsOpeningRef.current) setCurrentAiText(pending[pending.length - 1].text);
    // Compute from the ref synchronously so transcriptRef is up-to-date before
    // persistTranscript() runs. The setTranscript functional updater runs later
    // (React batching), so it can't be relied on to update the ref in time.
    let next = [...transcriptRef.current];
    // Remove any individually-added entries for the pending items, then append all
    // pending entries in order so natural pauses/splits remain visible in transcript.
    for (const entry of pending) {
      const idx = next.findIndex((e) => e.id === entry.id && e.role === 'assistant');
      if (idx >= 0) next.splice(idx, 1);
    }
    next = [...next, ...pending];
    transcriptRef.current = next;
    setTranscript(next);
    pendingAiEntriesRef.current = [];
    setStreamingAiText('');
    persistTranscript();
      }, [persistTranscript]);

  const safeCancelResponse = useCallback((reason: string) => {
    if (!isResponseActiveRef.current) return;
    try {
      sessionRef.current?.transport.sendEvent({ type: 'response.cancel' });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.toLowerCase().includes('no active response')) {
        console.log(`[diag] response.cancel race ignored reason=${reason} message=${message}`);
      } else {
        console.log(`[diag] response.cancel failed reason=${reason}`, err);
      }
    } finally {
      isResponseActiveRef.current = false;
    }
  }, []);

  const enterClosingUI = useCallback(() => {
    if (phaseRef.current === 'closing') return;
    phaseRef.current = 'closing';
    setPhase('closing');
    setCurrentAiText('');
  }, []);

  const sendResponseCreate = useCallback((source: string) => {
    if (userPausedRef.current) {
      console.log(`[pause] blocked response.create source=${source}`);
      return;
    }
    // Never stack creates — concurrent create causes "active response in progress"
    // and can interleave unrelated assistant turns. Drop duplicates instead.
    if (
      pendingCreateSourceRef.current
      || isResponseActiveRef.current
      || isAiRespondingRef.current
    ) {
      console.log(
        `[diag] dropped response.create source=${source} (pendingCreate=${pendingCreateSourceRef.current ?? 'none'} active=${isResponseActiveRef.current} isAiResponding=${isAiRespondingRef.current} id=${currentResponseIdRef.current ?? 'none'})`,
      );
      return;
    }
    console.log(
      `[diag] response.create source=${source} phase=${phaseRef.current} isAiResponding=${isAiRespondingRef.current} isResponseActive=${isResponseActiveRef.current} currentResponseId=${currentResponseIdRef.current ?? 'none'}`,
    );
    sessionRef.current?.transport.sendEvent({ type: 'response.create' });
    isAiRespondingRef.current = true; // guard the gap until response.created

    // Watchdog: if response.created never arrives, recover mic/UI without retrying
    // create (a second create can collide with a late first response).
    pendingCreateSourceRef.current = source;
    pendingCreateAtRef.current = performance.now();
    if (createStallTimerRef.current) {
      clearTimeout(createStallTimerRef.current);
      createStallTimerRef.current = null;
    }
    createStallTimerRef.current = setTimeout(() => {
      createStallTimerRef.current = null;
      if (!pendingCreateSourceRef.current) return;
      const stalledSource = pendingCreateSourceRef.current;
      const waited = performance.now() - pendingCreateAtRef.current;
      pendingCreateSourceRef.current = null;
      pendingCreateAtRef.current = 0;
      // Late response.created may still arrive; if it does, it will re-arm the turn.
      if (isResponseActiveRef.current || currentResponseIdRef.current) {
        console.warn(
          `[diag] response.create STALL source=${stalledSource} waitedMs=${waited.toFixed(0)} — response already active, leaving turn state`,
        );
        return;
      }
      console.warn(
        `[diag] response.create STALL source=${stalledSource} waitedMs=${waited.toFixed(0)} — recovering UI (no auto-retry)`,
      );
      isAiRespondingRef.current = false;
      applyMicOpen(true);
      setOrbState('listening');
    }, RESPONSE_CREATE_STALL_MS);
  }, [applyMicOpen]);

  const clearPendingCreateWatch = useCallback(() => {
    if (createStallTimerRef.current) {
      clearTimeout(createStallTimerRef.current);
      createStallTimerRef.current = null;
    }
    if (pendingCreateSourceRef.current && pendingCreateAtRef.current) {
      const waited = performance.now() - pendingCreateAtRef.current;
      console.log(
        `[diag] response.created after ${waited.toFixed(0)}ms source=${pendingCreateSourceRef.current}`,
      );
    }
    pendingCreateSourceRef.current = null;
    pendingCreateAtRef.current = 0;
  }, []);

  const finalizeInterview = useCallback((reason: string) => {
    if (interviewFinalizedRef.current) return;
    interviewFinalizedRef.current = true;
    console.log(`[ending] finalizeInterview via ${reason}`);
    
    phaseRef.current = 'closing';
    setPhase('closing');

    if (speechTimerRef.current) {
      clearTimeout(speechTimerRef.current);
      speechTimerRef.current = null;
    }
    if (gateTimeoutTimerRef.current) {
      clearTimeout(gateTimeoutTimerRef.current);
      gateTimeoutTimerRef.current = null;
    }
    pendingTurnRef.current = null;
    if (micRenableTimerRef.current) {
      clearTimeout(micRenableTimerRef.current);
      micRenableTimerRef.current = null;
    }
    if (aiDoneHardTimerRef.current) {
      clearTimeout(aiDoneHardTimerRef.current);
      aiDoneHardTimerRef.current = null;
    }
    if (stuckThinkingWatchdogRef.current) {
      clearTimeout(stuckThinkingWatchdogRef.current);
      stuckThinkingWatchdogRef.current = null;
    }
    if (createStallTimerRef.current) {
      clearTimeout(createStallTimerRef.current);
      createStallTimerRef.current = null;
    }
    pendingCreateSourceRef.current = null;
    pendingCreateAtRef.current = 0;

    // Prevent any late speech_stopped path from generating another response turn.
    isAiRespondingRef.current = true;
    deferFinalizeRef.current = false;
    responseHasAudioRef.current = false;
    closingPlaybackSeenRef.current = false;
    awaitingClosingFollowUpRef.current = false;
    suppressClosingFollowUpRef.current = false;

    flushPendingAiEntries();
    setCurrentAiText('');
    setStreamingAiText('');
    setEnded(true);
    setOrbState('idle');

    // Stop recording and microphone capture immediately when closing is complete.
    stopRecorder(mixedRecorderRef.current);
    stopUserTurnCapture();
    mixedRecorderRef.current = null;
    micStreamRef.current?.getTracks().forEach((t) => t.stop());

    // Disable server-side VAD so no new turns are auto-created.
    sessionRef.current?.transport.sendEvent({
      type: 'session.update',
      session: { type: 'realtime', audio: { input: { turn_detection: null } } },
    });

    const completedSessionId = sessionIdRef.current;
    if (completedSessionId) {
      onCompletedRef.current?.({ sessionId: completedSessionId });
    }
  }, [flushPendingAiEntries, stopUserTurnCapture]);

  // Slow-paced estimated client playback — hard-timeout safety net only.
  const estimatedPlaybackMs = useCallback(
    () => Math.max(
      aiResponseCharsRef.current * HARD_FALLBACK_MS_PER_CHAR + HARD_FALLBACK_PAD_MS,
      HARD_AI_DONE_MIN_MS,
    ),
    [],
  );

  const clearStuckThinkingWatchdog = useCallback(() => {
    if (stuckThinkingWatchdogRef.current) {
      clearTimeout(stuckThinkingWatchdogRef.current);
      stuckThinkingWatchdogRef.current = null;
    }
  }, []);

  const clearPlaybackWatchers = useCallback(() => {
    if (micRenableTimerRef.current) {
      clearTimeout(micRenableTimerRef.current);
      micRenableTimerRef.current = null;
    }
    if (aiDoneHardTimerRef.current) {
      clearTimeout(aiDoneHardTimerRef.current);
      aiDoneHardTimerRef.current = null;
    }
    clearStuckThinkingWatchdog();
    awaitingHardAfterTranscriptRef.current = false;
  }, [clearStuckThinkingWatchdog]);

  // Remaining estimated playback (response.done often arrives early on WebRTC).
  const remainingPlaybackFallbackMs = useCallback(() => {
    const estimate = estimatedPlaybackMs();
    const startedAt = aiResponseStartedAtRef.current;
    const elapsed = startedAt > 0 ? performance.now() - startedAt : 0;
    const remaining = Math.max(estimate - elapsed, HARD_AI_DONE_MIN_MS);
    return Math.min(remaining, HARD_AI_DONE_MAX_MS);
  }, [estimatedPlaybackMs]);

  // Primary turn boundary: called from output_audio_buffer.stopped (preferred),
  // with hard setTimeout as fallback when buffer.stopped is missing.
  const confirmAiDone = useCallback((source: string) => {
    if (aiTurnConfirmedDoneRef.current || interviewFinalizedRef.current) return;
    if (currentResponseIsOpeningRef.current) {
      const startedAt = outputBufferStartedAtRef.current;
      const stoppedAt = outputBufferStoppedAtRef.current;
      const startedToStoppedMs =
        startedAt > 0 && stoppedAt > startedAt ? stoppedAt - startedAt : -1;
      console.log(
        `[turn] opening_turn_summary chars=${aiResponseCharsRef.current}`
          + ` started_to_stopped_ms=${startedToStoppedMs >= 0 ? startedToStoppedMs.toFixed(0) : 'n/a'}`
          + ` confirm_source=${source}`
          + ` premature=${prematureBufferStoppedRef.current ? 1 : 0}`,
      );
    }
    clearPlaybackWatchers();
    aiTurnConfirmedDoneRef.current = true;
    isAiRespondingRef.current = false;
    console.log(`[turn] AI confirmed done via ${source}`);

    if (deferFinalizeRef.current) {
      finalizeInterview(`confirm.${source}`);
      return;
    }

    flushPendingAiEntries();
    applyMicOpen(true);
    setOrbState('listening');
  }, [applyMicOpen, clearPlaybackWatchers, finalizeInterview, flushPendingAiEntries]);

  // Warn-only companion to hard fallback. If confirmAiDone never runs (hard missed
  // or never armed), this surfaces stuck-thinking without changing turn behaviour.
  const scheduleStuckThinkingWatchdog = useCallback((hardMs: number, source: string) => {
    clearStuckThinkingWatchdog();
    const ms = hardMs + STUCK_THINKING_WATCHDOG_PAD_MS;
    const responseId = currentResponseIdRef.current;
    const phase = phaseRef.current;
    const chars = aiResponseCharsRef.current;
    const isOpening = currentResponseIsOpeningRef.current;
    stuckThinkingWatchdogRef.current = setTimeout(() => {
      stuckThinkingWatchdogRef.current = null;
      if (aiTurnConfirmedDoneRef.current || interviewFinalizedRef.current) return;
      console.warn(
        `[turn] stuck_thinking_watchdog source=${source}`
          + ` responseId=${responseId ?? 'none'}`
          + ` phase=${phase}`
          + ` opening=${isOpening ? 1 : 0}`
          + ` chars=${chars}`
          + ` waited_ms=${ms}`
          + ` isAiResponding=${isAiRespondingRef.current}`
          + ` (no confirmAiDone yet — warn only)`,
      );
    }, ms);
  }, [clearStuckThinkingWatchdog]);

  const scheduleHardAiDoneFallback = useCallback((ms: number, source: string) => {
    if (aiDoneHardTimerRef.current) {
      clearTimeout(aiDoneHardTimerRef.current);
      aiDoneHardTimerRef.current = null;
    }
    aiDoneHardTimerRef.current = setTimeout(() => {
      aiDoneHardTimerRef.current = null;
      if (aiTurnConfirmedDoneRef.current || interviewFinalizedRef.current) return;
      console.log(`[turn] hard fallback firing source=${source} after ${ms}ms`);
      confirmAiDone(source);
    }, ms);
    scheduleStuckThinkingWatchdog(ms, source);
  }, [confirmAiDone, scheduleStuckThinkingWatchdog]);

  // Prefer arming hard only once transcript length is known (slow estimate).
  // If terminal arrives first, wait for transcript.done (with a max-cap interim net).
  const armHardFallbackWhenReady = useCallback((source: string) => {
    if (aiTurnConfirmedDoneRef.current || interviewFinalizedRef.current) return;
    if (!responseHasAudioRef.current) return;
    if (aiResponseCharsRef.current < 5) {
      awaitingHardAfterTranscriptRef.current = true;
      console.log(
        `[turn] defer hard schedule until transcript.done source=${source} (interim max ${HARD_AI_DONE_MAX_MS}ms)`,
      );
      scheduleHardAiDoneFallback(HARD_AI_DONE_MAX_MS, 'hard.awaiting_transcript');
      return;
    }
    awaitingHardAfterTranscriptRef.current = false;
    const ms = remainingPlaybackFallbackMs();
    console.log(
      `[turn] scheduling hard fallback source=${source} ms=${ms.toFixed(0)} chars=${aiResponseCharsRef.current}`,
    );
    scheduleHardAiDoneFallback(ms, source);
  }, [remainingPlaybackFallbackMs, scheduleHardAiDoneFallback]);

  const handleEndInterviewSignal = useCallback((source: 'added' | 'done') => {
    enterClosingUI();
    const sameResponseHasAudio = responseHasAudioRef.current;
    if (sameResponseHasAudio) {
      suppressClosingFollowUpRef.current = true;
      deferFinalizeRef.current = true;
    } else {
      awaitingClosingFollowUpRef.current = true;
      deferFinalizeRef.current = false;
    }
    if (source !== 'done') return;
    if (sameResponseHasAudio) {
      if (aiTurnConfirmedDoneRef.current && closingPlaybackSeenRef.current) {
        finalizeInterview('end_tool.playback_already_done');
      } else {
        console.log('[ending] defer finalize until output_audio_buffer.stopped / playback done');
      }
    } else {
      console.log('[ending] end_interview signal — awaiting follow-up closing speech');
    }
  }, [enterClosingUI, finalizeInterview]);

  /** Drop one user utterance from client transcript + server conversation. */
  const dropUserUtterance = useCallback((opts: {
    reason: string;
    itemId?: string | null;
    placeholderId?: string | null;
    /** Cancel pending grace/create for this turn (not the content check itself). */
    clearCreateBarriers?: boolean;
  }) => {
    const { reason, itemId = null, placeholderId = null, clearCreateBarriers = false } = opts;
    console.log(
      `[turn-gate] DROP reason=${reason}`
        + ` item=${itemId ?? 'none'}`
        + ` placeholder=${placeholderId ?? 'none'}`
        + ` clearCreate=${clearCreateBarriers ? 1 : 0}`,
    );

    const itemIdsToDelete = new Set<string>();
    if (placeholderId) {
      discardedPlaceholderIdsRef.current.add(placeholderId);
      pendingUserPlaceholderQueueRef.current = pendingUserPlaceholderQueueRef.current.filter(
        (id) => id !== placeholderId,
      );
      removeUserTranscriptEntry(placeholderId);
      for (const [mappedItemId, mapped] of Object.entries(pendingUserPlaceholderByItemIdRef.current)) {
        if (mapped === placeholderId) {
          discardedUserItemIdsRef.current.add(mappedItemId);
          itemIdsToDelete.add(mappedItemId);
          delete pendingUserPlaceholderByItemIdRef.current[mappedItemId];
          removeUserTranscriptEntry(mappedItemId);
        }
      }
    }
    if (itemId) {
      discardedUserItemIdsRef.current.add(itemId);
      itemIdsToDelete.add(itemId);
      delete pendingUserPlaceholderByItemIdRef.current[itemId];
      removeUserTranscriptEntry(itemId);
    }
    for (const id of itemIdsToDelete) {
      deleteServerConversationItem(id);
    }
    if (itemId && userTurnPendingItemIdRef.current === itemId) {
      userTurnPendingItemIdRef.current = null;
      userTurnPendingBlobRef.current = null;
    }
    if (clearCreateBarriers) {
      clearPendingTurnBarriers();
    }
    if (!userSpeakingRef.current) {
      setOrbState('listening');
    }
    persistTranscript();
  }, [clearPendingTurnBarriers, deleteServerConversationItem, persistTranscript, removeUserTranscriptEntry]);

  const tryProceedPendingTurn = useCallback(() => {
    const pending = pendingTurnRef.current;
    if (!pending) return;

    if (pending.needsGate && pending.gateResolved && !pending.gatePassed) {
      // Timeout / unresolved transcript — drop the pending placeholder turn.
      dropUserUtterance({
        reason: 'gate_reject',
        placeholderId: pending.placeholderId,
        clearCreateBarriers: true,
      });
      return;
    }

    if (!(pending.graceDone && pending.gateResolved && pending.gatePassed)) {
      console.log(
        `[turn-gate] waiting graceDone=${pending.graceDone}`
          + ` gateResolved=${pending.gateResolved}`
          + ` gatePassed=${pending.gatePassed}`
          + ` needsGate=${pending.needsGate}`
          + ` durationMs=${pending.durationMs}`,
      );
      return;
    }

    const source = pending.needsGate
      ? 'turn_gate.grace_and_transcript'
      : 'vad.speech_stopped_timer';
    console.log(
      `[turn-gate] PASS source=${source} durationMs=${pending.durationMs} needsGate=${pending.needsGate}`,
    );
    clearPendingTurnBarriers();
    if (isAiRespondingRef.current) {
      console.log('[turn-gate] skip create — AI started while barriers pending');
      return;
    }
    flushPendingAiEntries();
    setOrbState('thinking');
    sendResponseCreate(source);
  }, [clearPendingTurnBarriers, dropUserUtterance, flushPendingAiEntries, sendResponseCreate]);

  // ── Raw event handler (wired to transport.on('*', ...)) ───────────────────

  const handleRawEvent = useCallback(
    (event: unknown) => {
      const msg = event as Record<string, unknown>;
      const type = msg.type as string;

      if (type && WATCHED_REALTIME_EVENTS.has(type)) {
        console.log('[realtime-watch]', {
          timestamp: performance.now(),
          wallClock: new Date().toISOString(),
          eventType: type,
          event: msg,
          visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
          phase: phaseRef.current,
          isAiResponding: isAiRespondingRef.current,
          isResponseActive: isResponseActiveRef.current,
          orbHint: 'see React state / UI label',
        });
      }

      switch (type) {
        // AI is generating — reset per-turn state. Primary done signal is
        // output_audio_buffer.stopped; hard timeout is the only fallback.
        case 'response.created': {
          if (interviewFinalizedRef.current) {
            safeCancelResponse('response.created.after_finalize');
            break;
          }
          if (suppressClosingFollowUpRef.current) {
            suppressClosingFollowUpRef.current = false;
            console.log('[ending] suppressing duplicate closing follow-up response');
            safeCancelResponse('closing.suppress_duplicate_followup');
            break;
          }
          const id = (msg.response as Record<string, unknown> | null)?.id as string | undefined;
          currentResponseIdRef.current = id ?? null;
          console.log(`[diag] response.created id=${id ?? 'unknown'} phase=${phaseRef.current}`);
          clearPendingCreateWatch();
          setAiTurnId(id ?? String(Date.now()));
          setCurrentAiText('');
          setStreamingAiText('');
          setOrbState('thinking');
          isAiRespondingRef.current = true;
          isResponseActiveRef.current = true;
          aiResponseCharsRef.current = 0;
          aiTurnConfirmedDoneRef.current = false;
          responseHasAudioRef.current = false;
          closingPlaybackSeenRef.current = false;
          aiResponseStartedAtRef.current = performance.now();
          outputBufferStartedAtRef.current = 0;
          outputBufferStoppedAtRef.current = 0;
          prematureBufferStoppedRef.current = false;
          currentResponseIsOpeningRef.current = phaseRef.current === 'opening';
          clearPlaybackWatchers();
          if (speechTimerRef.current) {
            clearTimeout(speechTimerRef.current);
            speechTimerRef.current = null;
          }
          // Mute while the AI speaks; re-opened in confirmAiDone with the UI flip.
          applyMicOpen(false);
          // Apply interviewing VAD once the first post-opening response exists —
          // never mid-create with a bare 1s timer (that raced / perturbed turns).
          if (phaseRef.current === 'interviewing' && !interviewingVadAppliedRef.current) {
            interviewingVadAppliedRef.current = true;
            console.log('[opening] applying interviewing VAD on first interviewing response.created');
            sessionRef.current?.transport.sendEvent({
              type: 'session.update',
              session: { type: 'realtime', audio: { input: { turn_detection: INTERVIEWING_VAD_CONFIG } } },
            });
          }
          break;
        }

        // WebRTC: server started draining output audio to the client.
        case 'output_audio_buffer.started': {
          if (interviewFinalizedRef.current) break;
          responseHasAudioRef.current = true;
          outputBufferStartedAtRef.current = performance.now();
          setOrbState('speaking');
          if (phaseRef.current === 'closing' || deferFinalizeRef.current) {
            closingPlaybackSeenRef.current = true;
            awaitingClosingFollowUpRef.current = false;
            deferFinalizeRef.current = true;
          }
          break;
        }

        // WebRTC primary turn boundary — validated accurate when started→stopped spans real playback.
        case 'output_audio_buffer.stopped': {
          if (interviewFinalizedRef.current) break;
          if (aiTurnConfirmedDoneRef.current) break;
          const startedAt = outputBufferStartedAtRef.current;
          const sinceStarted = startedAt > 0 ? performance.now() - startedAt : Number.POSITIVE_INFINITY;
          // Batched event bursts can emit started+stopped within ms of response.created
          // before any audio is heard — ignore those and wait for estimate/hard fallback.
          if (responseHasAudioRef.current && sinceStarted < PREMATURE_BUFFER_STOPPED_MS) {
            prematureBufferStoppedRef.current = true;
            console.warn(
              `[turn] ignoring premature output_audio_buffer.stopped (sinceStarted=${sinceStarted.toFixed(0)}ms)`,
            );
            armHardFallbackWhenReady('hard.after_premature_stopped');
            break;
          }
          // Mic + UI flip together via confirmAiDone — no settle gap that leaves
          // mic open while isAiResponding is still true.
          outputBufferStoppedAtRef.current = performance.now();
          console.log('[turn] output_audio_buffer.stopped — confirming AI done');
          confirmAiDone('output_audio_buffer.stopped');
          break;
        }

        case 'output_audio_buffer.cleared': {
          // Cleared mid-response is noisy (cancel / glitch). Only treat as turn-end
          // once the API response is already inactive — otherwise wait for
          // response.cancelled, a real buffer.stopped, or hard fallback.
          if (interviewFinalizedRef.current || aiTurnConfirmedDoneRef.current) break;
          if (isResponseActiveRef.current) {
            console.log('[turn] output_audio_buffer.cleared ignored — response still active');
            break;
          }
          console.log('[turn] output_audio_buffer.cleared — confirming AI done');
          confirmAiDone('output_audio_buffer.cleared');
          break;
        }

        // Streaming AI transcript — only accumulate; state is flushed after AI done
        case 'response.output_audio_transcript.delta': {
          if (interviewFinalizedRef.current) break;
          if (phaseRef.current === 'opening' && openingAssistantItemSeenRef.current) {
            safeCancelResponse('opening.delta_guard');
            break;
          }
          const itemId = msg.item_id as string;
          const delta = msg.delta as string;
          if (!delta) break;
          const accumulated = (partialRef.current[itemId] ?? '') + delta;
          partialRef.current[itemId] = accumulated;
          aiResponseCharsRef.current += delta.length;
          setStreamingAiText(accumulated);
          setOrbState('speaking');
          responseHasAudioRef.current = true;
          if (phaseRef.current === 'closing') {
            awaitingClosingFollowUpRef.current = false;
            deferFinalizeRef.current = true;
          }
          break;
        }

        // AI transcript finalised — park in pending; shown on confirmAiDone.
        case 'response.output_audio_transcript.done': {
          if (interviewFinalizedRef.current) break;
          if (phaseRef.current === 'opening' && openingAssistantItemSeenRef.current) {
            safeCancelResponse('opening.done_guard');
            console.log('[opening] ignored extra assistant item before user reply');
            break;
          }
          const itemId = msg.item_id as string;
          const text = msg.transcript as string;
          console.log(`[diag] transcript.done item=${itemId} textLen=${text?.length ?? 0} opening=${phaseRef.current === 'opening'}`);
          if (text) {
            // Prefer final transcript length for slow hard-estimate (deltas may lag).
            aiResponseCharsRef.current = Math.max(aiResponseCharsRef.current, text.length);
            if (phaseRef.current === 'opening') {
              openingAssistantItemSeenRef.current = true;
            }
            pendingAiEntriesRef.current.push({
              id: itemId,
              role: 'assistant',
              text,
              // Stamp at finalisation time. Opening responses are created while
              // phase is still opening (currentResponseIsOpeningRef), even if
              // flush happens after the user has flipped to interviewing.
              phase: currentResponseIsOpeningRef.current ? 'opening' : phaseRef.current,
              timestamp: new Date().toISOString(),
            });
          }
          delete partialRef.current[itemId];
          // Arm / refresh hard fallback once length is known (after response.done, or if
          // terminal already passed and we were waiting on transcript).
          if (
            awaitingHardAfterTranscriptRef.current
            || (!isResponseActiveRef.current && responseHasAudioRef.current && isAiRespondingRef.current)
          ) {
            armHardFallbackWhenReady('hard.after_transcript_done');
          }
          break;
        }

        // Server finished the response. Prefer waiting for output_audio_buffer.stopped;
        // arm hard fallback / no-audio confirm if buffer events are absent.
        case 'response.output_audio.done':
        case 'response.done': {
          const responseId = (msg.response as Record<string, unknown> | null)?.id as string | undefined;
          console.log(`[diag] response.terminal type=${type} id=${responseId ?? 'unknown'} current=${currentResponseIdRef.current ?? 'none'}`);
          if (!responseId || responseId === currentResponseIdRef.current) {
            currentResponseIdRef.current = null;
          }
          isResponseActiveRef.current = false;
          // Tool-only end_interview turn: SDK follow-up carries closing speech — don't confirm yet.
          if (awaitingClosingFollowUpRef.current && !responseHasAudioRef.current) {
            clearPlaybackWatchers();
            break;
          }
          if (phaseRef.current === 'closing' && responseHasAudioRef.current && !deferFinalizeRef.current) {
            deferFinalizeRef.current = true;
          }
          if (responseHasAudioRef.current) {
            setOrbState((prev) => (prev === 'thinking' ? 'speaking' : prev));
            // Only arm the slow hard estimate on response.done (not output_audio.done),
            // and only once transcript length looks complete — otherwise wait.
            if (type === 'response.done') {
              armHardFallbackWhenReady('hard.timeout_after_response_done');
            }
          } else if (type === 'response.done') {
            // No audio in this turn — buffer.stopped will not fire.
            scheduleHardAiDoneFallback(NO_AUDIO_RESPONSE_DONE_MS, 'hard.no_audio_response_done');
          }
          break;
        }

        // AI response cancelled — clear watchers and do a short drain wait
        case 'response.cancelled':
          if (interviewFinalizedRef.current) break;
          {
            const responseId = (msg.response as Record<string, unknown> | null)?.id as string | undefined;
            console.log(`[diag] response.cancelled id=${responseId ?? 'unknown'} current=${currentResponseIdRef.current ?? 'none'}`);
            if (!responseId || responseId === currentResponseIdRef.current) {
              currentResponseIdRef.current = null;
            }
          }
          isResponseActiveRef.current = false;
          clearPlaybackWatchers();
          if (!micRenableTimerRef.current) {
            micRenableTimerRef.current = setTimeout(() => {
              micRenableTimerRef.current = null;
              isAiRespondingRef.current = false;
              flushPendingAiEntries();
              setOrbState('listening');
              applyMicOpen(true);
            }, 300);
          }
          break;

        // Ignore speech while the AI turn is open (mic is muted). Residual VAD must
        // not pollute spokenAt queues or flip userSpeakingRef.
        case 'input_audio_buffer.speech_started':
          if (userPausedRef.current) break;
          if (isAiRespondingRef.current) {
            console.log('[vad] ignoring speech_started — AI still responding');
            break;
          }
          // User resumed speaking — cancel pending response.create / grace only.
          // Per-item content checks still run when each transcription arrives.
          clearPendingTurnBarriers();
          userSpeakingRef.current = true;
          utteranceStartedAtMsRef.current = performance.now();
          const speechStartedAt = new Date().toISOString();
          pendingUserSpeechStartTimesRef.current.push(speechStartedAt);
          // Insert a placeholder entry NOW so the user turn sits before the AI
          // response that will follow. The placeholder is replaced in-place when
          // the real transcription arrives (which is always later).
          {
            const pendingId = `user-pending-${Date.now()}`;
            utterancePlaceholderIdRef.current = pendingId;
            pendingUserPlaceholderQueueRef.current.push(pendingId);
            const nextWithPlaceholder: TranscriptEntry[] = [
              ...transcriptRef.current,
              {
                id: pendingId,
                role: 'user',
                text: '…',
                phase: phaseRef.current,
                timestamp: speechStartedAt,
                spokenAt: speechStartedAt,
              },
            ];
            transcriptRef.current = nextWithPlaceholder;
            setTranscript(nextWithPlaceholder);
            setOrbState('listening');
            startUserTurnCapture();
          }
          break;

        // Associates committed user item_id with the earliest placeholder captured
        // for this utterance so transcription.completed can replace the right entry.
        case 'input_audio_buffer.committed': {
          const itemId = msg.item_id as string | undefined;
          if (itemId && !pendingUserPlaceholderByItemIdRef.current[itemId]) {
            const placeholderId = pendingUserPlaceholderQueueRef.current.shift();
            if (placeholderId) {
              if (discardedPlaceholderIdsRef.current.has(placeholderId)) {
                discardedUserItemIdsRef.current.add(itemId);
              } else {
                pendingUserPlaceholderByItemIdRef.current[itemId] = placeholderId;
              }
            }
          }
          if (itemId && discardedUserItemIdsRef.current.has(itemId)) {
            // Discarded short/noise turn — purge from server history and skip upload.
            deleteServerConversationItem(itemId);
            userTurnPendingItemIdRef.current = null;
            userTurnPendingBlobRef.current = null;
            break;
          }
          if (itemId) {
            userTurnPendingItemIdRef.current = itemId;
            tryUploadUserTurn();
          }
          break;
        }

        case 'input_audio_buffer.speech_stopped':
          if (userPausedRef.current) break;
          // Ignore spurious speech_stopped events that can occur without a
          // matching speech_started (e.g. VAD jitter / ambient glitches). If we
          // react to those, we may send response.create with no new user input,
          // causing the assistant to speak twice in a row.
          if (!userSpeakingRef.current) {
            console.log('[vad] ignoring speech_stopped without active user speech');
            break;
          }
          userSpeakingRef.current = false;
          stopUserTurnCapture();
          // Residual/spurious while AI turn is still open — ignore.
          if (isAiRespondingRef.current) {
            console.log('[vad] ignoring speech_stopped — AI still responding');
            break;
          }
          // Opening transitions only after real user transcription is completed.
          if (phaseRef.current === 'opening') break;
          if (phaseRef.current === 'closing') break;

          {
            const startedAt = utteranceStartedAtMsRef.current;
            const durationMs = startedAt > 0
              ? Math.max(0, performance.now() - startedAt)
              : Number.POSITIVE_INFINITY;
            const needsGate = durationMs < SHORT_UTTERANCE_MS;
            const placeholderId = utterancePlaceholderIdRef.current;
            pendingTurnRef.current = {
              needsGate,
              graceDone: false,
              gateResolved: !needsGate,
              gatePassed: !needsGate,
              placeholderId,
              durationMs: Number.isFinite(durationMs) ? durationMs : -1,
            };
            console.log(
              `[turn-gate] speech_stopped durationMs=${pendingTurnRef.current.durationMs}`
                + ` needsGate=${needsGate} placeholder=${placeholderId ?? 'none'}`,
            );

            // Grace period runs for every interviewing turn. speech_started cancels it.
            speechTimerRef.current = setTimeout(() => {
              speechTimerRef.current = null;
              if (isAiRespondingRef.current) {
                console.log('[vad] ignoring speech_stopped timer — AI started during grace');
                clearPendingTurnBarriers();
                return;
              }
              const pending = pendingTurnRef.current;
              if (!pending) return;
              pending.graceDone = true;
              tryProceedPendingTurn();
            }, RESPONSE_GRACE_MS);

            // Short utterances also wait for a usable transcript (AND with grace).
            if (needsGate) {
              clearGateTimeoutTimer();
              gateTimeoutTimerRef.current = setTimeout(() => {
                gateTimeoutTimerRef.current = null;
                const pending = pendingTurnRef.current;
                if (!pending || pending.gateResolved) return;
                console.warn(
                  `[turn-gate] transcript timeout after ${RESPONSE_GRACE_MS + TURN_GATE_TIMEOUT_PAD_MS}ms — DROP`,
                );
                pending.gateResolved = true;
                pending.gatePassed = false;
                tryProceedPendingTurn();
              }, RESPONSE_GRACE_MS + TURN_GATE_TIMEOUT_PAD_MS);
            }
          }
          break;

        case 'response.output_item.added': {
          const item = msg.item as Record<string, unknown> | null;
          if (item?.type === 'function_call' && (item?.name as string) === 'end_interview') {
            handleEndInterviewSignal('added');
          }
          break;
        }

        case 'response.output_item.done': {
          const item = msg.item as Record<string, unknown> | null;
          if (item?.type === 'function_call') {
            const responseId = currentResponseIdRef.current ?? 'unknown';
            console.log(`[diag] tool.done name=${String(item?.name ?? 'unknown')} resp=${responseId} argsPresent=${typeof item?.arguments === 'string'}`);
          }
          if (item?.type === 'function_call' && item?.name === 'end_interview') {
            handleEndInterviewSignal('done');
          }
          break;
        }

        // User speech transcription — arrives after the AI has often already started
        // responding. We replace the placeholder inserted at speech_started in-place
        // so the user turn stays before the AI response in the list.
        case 'conversation.item.input_audio_transcription.completed': {
          const itemId = msg.item_id as string;
          const text = (msg.transcript as string | undefined)?.trim();
          const transcribedAt = new Date().toISOString();
          const pendingAudioFile = pendingAudioFileByItemIdRef.current[itemId];
          if (pendingAudioFile) delete pendingAudioFileByItemIdRef.current[itemId];
          const pendingId =
            pendingUserPlaceholderByItemIdRef.current[itemId]
            ?? pendingUserPlaceholderQueueRef.current.shift()
            ?? null;

          // Turns dropped by the short-utterance gate must not reappear via fallback append.
          if (
            discardedUserItemIdsRef.current.has(itemId)
            || (pendingId != null && discardedPlaceholderIdsRef.current.has(pendingId))
          ) {
            discardedUserItemIdsRef.current.add(itemId);
            if (pendingId) discardedPlaceholderIdsRef.current.add(pendingId);
            if (pendingUserPlaceholderByItemIdRef.current[itemId]) {
              delete pendingUserPlaceholderByItemIdRef.current[itemId];
            }
            // Late transcription for an already-dropped turn — still purge server history.
            deleteServerConversationItem(itemId);
            console.log(`[turn-gate] ignoring transcription for discarded item=${itemId}`);
            break;
          }

          if (pendingUserPlaceholderByItemIdRef.current[itemId]) {
            delete pendingUserPlaceholderByItemIdRef.current[itemId];
          }
          // Update transcript display
          {
            // Compute synchronously from the ref so transcriptRef is current before
            // persistTranscript() runs (see flushPendingAiEntries note).
            const cur = transcriptRef.current;
            if (!cur.find((e) => e.id === itemId && e.role === 'user')) {
              const placeholderIdx = pendingId
                ? cur.findIndex((e) => e.id === pendingId)
                : cur.findIndex((e) => e.role === 'user' && e.id.startsWith('user-pending-'));
              const placeholder = placeholderIdx >= 0 ? cur[placeholderIdx] : null;
              const spokenAt =
                placeholder?.spokenAt
                ?? placeholder?.timestamp
                ?? pendingUserSpeechStartTimesRef.current.shift()
                ?? transcribedAt;
              // Stamp with the phase at utterance time (opening ready-reply stays
              // opening). Session advances to interviewing after a usable opening reply.
              const turnPhase = phaseRef.current;
              let next: TranscriptEntry[];
              if (!text) {
                // No usable transcription — remove placeholder
                if (placeholderIdx >= 0) {
                  next = [...cur];
                  next.splice(placeholderIdx, 1);
                } else {
                  next = cur;
                }
              } else if (placeholderIdx >= 0) {
                // Replace in-place — keeps the user entry before the AI entries that follow
                next = [...cur];
                next[placeholderIdx] = {
                  ...next[placeholderIdx],
                  id: itemId,
                  role: 'user',
                  text,
                  phase: turnPhase,
                  timestamp: spokenAt,
                  spokenAt,
                  transcribedAt,
                  ...(next[placeholderIdx].audioFile || pendingAudioFile
                    ? { audioFile: next[placeholderIdx].audioFile ?? pendingAudioFile }
                    : {}),
                };
              } else {
                // Fallback: no placeholder found (e.g. speech started while AI was responding)
                next = [
                  ...cur,
                  {
                    id: itemId,
                    role: 'user',
                    text,
                    phase: turnPhase,
                    timestamp: spokenAt,
                    spokenAt,
                    transcribedAt,
                    ...(pendingAudioFile ? { audioFile: pendingAudioFile } : {}),
                  },
                ];
              }
              if (next !== cur) {
                transcriptRef.current = next;
                setTranscript(next);
              }
            }
            if (phaseRef.current === 'opening') {
              // Only leave opening on usable content (e.g. "Yes."), not filler/noise.
              if (!hasUsableTranscriptContent(text ?? '')) {
                dropUserUtterance({
                  reason: 'content_reject_opening',
                  itemId,
                  placeholderId: pendingId,
                });
              } else {
                phaseRef.current = 'interviewing';
                setPhase('interviewing');
                if (!isAiRespondingRef.current) {
                  flushPendingAiEntries();
                  setOrbState('thinking');
                  sendResponseCreate('transcription.opening_transition');
                } else {
                  // Rare: transcription arrived while opening audio still marked active.
                  console.log('[opening] skipped response.create — AI still responding');
                }
                if (text) persistTranscript();
              }
              // Interviewing VAD is applied on that response's response.created
              // (see handleRawEvent), not here mid-flight.
            } else if (phaseRef.current === 'interviewing') {
              // Content check runs for EVERY transcribed item, even if create-barriers
              // were cleared because the user resumed speaking.
              const usable = hasUsableTranscriptContent(text ?? '');
              const pendingTurn = pendingTurnRef.current;
              const matchesPending = Boolean(
                pendingTurn
                && (
                  !pendingTurn.placeholderId
                  || pendingTurn.placeholderId === pendingId
                  || pendingTurn.placeholderId === utterancePlaceholderIdRef.current
                ),
              );

              if (!usable) {
                console.log(
                  `[turn-gate] content reject text=${JSON.stringify(text ?? '')}`
                    + ` item=${itemId} matchesPending=${matchesPending ? 1 : 0}`,
                );
                dropUserUtterance({
                  reason: 'content_reject',
                  itemId,
                  placeholderId: pendingId,
                  clearCreateBarriers: matchesPending,
                });
              } else if (
                matchesPending
                && pendingTurn
                && pendingTurn.needsGate
                && !pendingTurn.gateResolved
              ) {
                pendingTurn.gateResolved = true;
                pendingTurn.gatePassed = true;
                console.log(
                  `[turn-gate] transcript gatePassed=true`
                    + ` text=${JSON.stringify(text)}`
                    + ` durationMs=${pendingTurn.durationMs}`,
                );
                tryProceedPendingTurn();
                if (text) persistTranscript();
              } else if (text) {
                persistTranscript();
              }
            } else if (text) {
              persistTranscript();
            }
          }
          break;
        }

        case 'conversation.item.deleted': {
          const deletedId = msg.item_id as string | undefined;
          console.log(`[turn-gate] confirmed conversation.item.deleted item=${deletedId ?? 'unknown'}`);
          break;
        }

        case 'error': {
          const errMsg =
            (msg.error as Record<string, unknown> | null)?.message as string | undefined
            ?? 'Unknown error from realtime API';
          const errLower = errMsg.toLowerCase();
          // Ignore benign "no active response" errors from response.cancel sent when
          // the response had already completed — these are race-condition artefacts
          // and must not flip the UI to the error screen.
          if (errLower.includes('no active response')) {
            console.log(`[diag] ignored benign error: ${errMsg}`);
            break;
          }
          // Agents SDK auto-sends conversation.item.retrieve on transcription.completed.
          // Immediate delete races that retrieve → "Error retrieving item: ... does not exist".
          if (errLower.includes('retrieving item')) {
            console.log(`[diag] ignored Error retrieving item (delete/retrieve race): ${errMsg}`);
            break;
          }
          if (
            errLower.includes('item')
            && (errLower.includes('not found') || errLower.includes('does not exist') || errLower.includes('unknown'))
          ) {
            console.log(`[diag] ignored benign missing-item error: ${errMsg}`);
            break;
          }
          // Concurrent create rejected — keep interview alive on the in-flight
          // response; do not queue another create after it finishes.
          if (errLower.includes('active response') && errLower.includes('in progress')) {
            console.warn(`[diag] ignored concurrent create: ${errMsg}`);
            isAiRespondingRef.current = true;
            isResponseActiveRef.current = true;
            clearPendingCreateWatch();
            setOrbState((prev) => (prev === 'idle' ? 'thinking' : prev));
            break;
          }
          setError(errMsg);
          setStatus('error');
          break;
        }
      }
    },
    [
      applyMicOpen,
      armHardFallbackWhenReady,
      clearGateTimeoutTimer,
      clearPendingTurnBarriers,
      clearPlaybackWatchers,
      clearPendingCreateWatch,
      confirmAiDone,
      deleteServerConversationItem,
      dropUserUtterance,
      flushPendingAiEntries,
      persistTranscript,
      finalizeInterview,
      handleEndInterviewSignal,
      remainingPlaybackFallbackMs,
      safeCancelResponse,
      scheduleHardAiDoneFallback,
      sendResponseCreate,
      startUserTurnCapture,
      stopUserTurnCapture,
      tryProceedPendingTurn,
      tryUploadUserTurn,
    ],
  );

  // ── start() ──────────────────────────────────────────────────────────────────

  const start = useCallback(
    async (
      voice = 'marin',
      existingStream?: MediaStream,
      noiseReductionType: 'near_field' | 'far_field' = 'far_field',
      outputDeviceId?: string,
    ) => {
      const transcriptionModel = getTranscriptionModelFromHostname(window.location.hostname);

      // Tear down any leaked prior RealtimeSession before opening a new one
      // (double-start / soft remount would otherwise mix two conversations).
      const priorSession = sessionRef.current;
      sessionRef.current = null;
      if (priorSession) {
        console.log('[diag] start() closing prior RealtimeSession before reconnect');
        try {
          priorSession.close();
        } catch (err) {
          console.log('[diag] prior session close error', err);
        }
      }
      if (createStallTimerRef.current) {
        clearTimeout(createStallTimerRef.current);
        createStallTimerRef.current = null;
      }
      pendingCreateSourceRef.current = null;
      pendingCreateAtRef.current = 0;
      interviewingVadAppliedRef.current = false;
      clearPlaybackWatchers();

      setStatus('connecting');
      setError(null);
      setEnded(false);
      setPhase('opening');
      setTranscript([]);
      transcriptRef.current = [];
      setCurrentAiText('');
      setAiTurnId('');
      partialRef.current = {};
      pendingUserPlaceholderQueueRef.current = [];
      pendingUserPlaceholderByItemIdRef.current = {};
      pendingUserSpeechStartTimesRef.current = [];
      pendingAiEntriesRef.current = [];
      interviewFinalizedRef.current = false;
      deferFinalizeRef.current = false;
      responseHasAudioRef.current = false;
      closingPlaybackSeenRef.current = false;
      awaitingClosingFollowUpRef.current = false;
      suppressClosingFollowUpRef.current = false;
      openingAssistantItemSeenRef.current = false;
      currentResponseIdRef.current = null;
      isAiRespondingRef.current = false;
      isResponseActiveRef.current = false;
      aiTurnConfirmedDoneRef.current = false;
      {
        const now = new Date();
        const p = (n: number) => String(n).padStart(2, '0');
        const datePart = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}`;
        const timePart = `${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`;
        const suffix = Math.random().toString(36).slice(2, 7);
        sessionIdRef.current = isDemoSession
          ? `demo-${datePart}-${timePart}-${suffix}`
          : `${recordingStudyId}-${datePart}-${timePart}-${suffix}`;
      }
      mixedRecorderRef.current = null;
      userTurnRecorderRef.current = null;
      remoteMixConnectedRef.current = false;
      userTurnPendingItemIdRef.current = null;
      userTurnPendingBlobRef.current = null;
      userTurnDiscardRef.current = false;
      pendingAudioFileByItemIdRef.current = {};
      if (speechTimerRef.current) { clearTimeout(speechTimerRef.current); speechTimerRef.current = null; }
      userSpeakingRef.current = false;
      phaseRef.current = 'opening';

      try {
        // 1. Get ephemeral key from server
        const sessResp = await fetch('/api/realtime/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ voice }),
        });
        if (!sessResp.ok) {
          const e = await sessResp.json().catch(() => ({ error: sessResp.statusText }));
          throw new Error((e as Record<string, string>).error ?? 'Failed to create session');
        }
        const sessData = await sessResp.json();
        const ephemeralKey = (sessData as Record<string, string>).value;
        if (!ephemeralKey) throw new Error('No ephemeral key in session response');

        // 2. Mic stream — reuse the one acquired during the audio check if provided,
        //    otherwise prompt and acquire with echo cancellation.
        let stream: MediaStream;
        if (existingStream) {
          stream = existingStream;
        } else {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
          } catch (micErr) {
            const name = micErr instanceof Error ? micErr.name : '';
            throw new Error(
              name === 'NotAllowedError'
                ? 'Microphone access was denied. Please allow microphone access and try again.'
                : 'Could not access microphone.',
            );
          }
        }
        micStreamRef.current = stream;

        // Mixed recording: mic + AI remote track routed into one MediaStream.
        const mixCtx = new AudioContext();
        mixCtxRef.current = mixCtx;
        await mixCtx.resume();
        const mixDestination = mixCtx.createMediaStreamDestination();
        mixDestinationRef.current = mixDestination;
        mixCtx.createMediaStreamSource(stream).connect(mixDestination);
        mixedRecorderRef.current = createStreamingRecorder(
          mixDestination.stream,
          (blob) => sendMixedAudioChunk(blob),
        );

        // Briefly mute on mousedown to prevent click sounds from triggering VAD.
        // Skipped if user manually muted or AI is currently responding.
        const handleMouseDown = () => {
          if (userMutedRef.current || userPausedRef.current || isAiRespondingRef.current) return;
          applyMicOpen(false);
          setTimeout(() => {
            if (!userMutedRef.current && !userPausedRef.current && !isAiRespondingRef.current) {
              applyMicOpen(true);
            }
          }, 150);
        };
        mouseDownHandlerRef.current = handleMouseDown;
        document.addEventListener('mousedown', handleMouseDown);

        // 3. Mic level polling for orb animation
        const micCtx = new AudioContext();
        micCtxRef.current = micCtx;
        const micAnalyser = micCtx.createAnalyser();
        micAnalyser.fftSize = 256;
        micCtx.createMediaStreamSource(stream).connect(micAnalyser);
        const levelBuf = new Uint8Array(micAnalyser.frequencyBinCount);
        const pollLevel = () => {
          micAnalyser.getByteFrequencyData(levelBuf);
          let sum = 0;
          for (let i = 0; i < levelBuf.length; i++) sum += levelBuf[i];
          const avg = sum / levelBuf.length / 128;
          liveLevelsRef.current.mic = Math.min(avg * 2.5, 1);
          rafRef.current = requestAnimationFrame(pollLevel);
        };
        rafRef.current = requestAnimationFrame(pollLevel);

        // 4. Audio element for remote AI playback — route to selected speaker if any
        const audioEl = new Audio();
        audioEl.autoplay = true;
        if (outputDeviceId && 'setSinkId' in audioEl) {
          (audioEl as HTMLAudioElement & { setSinkId(id: string): Promise<void> })
            .setSinkId(outputDeviceId).catch(console.error);
        }

        // 4b. Remote AudioContext for AnalyserNode (orb AI level).
        //     Created inside a user-gesture handler so the browser won't auto-suspend it.
        const remoteCtx = new AudioContext();
        remoteAudioCtxRef.current = remoteCtx;
        await remoteCtx.resume();

        // 5. WebRTC transport — changePeerConnection wires the AnalyserNode to the
        //    remote audio track once the peer connection receives it.
        //    createMediaStreamSource on the track's stream (not createMediaElementSource)
        //    is required: createMediaElementSource silently produces no data when the
        //    audio element's srcObject is a live WebRTC MediaStream in Chrome.
        //    The audio element continues playing normally via its own srcObject pipeline;
        //    the MediaStreamSource only taps it for analysis and does not need to connect
        //    to ctx.destination.
        const transport = new OpenAIRealtimeWebRTC({
          mediaStream: stream,
          audioElement: audioEl,
          changePeerConnection: async (pc: RTCPeerConnection) => {
            pc.addEventListener('track', (ev: RTCTrackEvent) => {
              const ctx = remoteAudioCtxRef.current;
              if (!ctx || remoteAnalyserRef.current) return;
              ctx.resume().catch(() => {});
              const analyser = ctx.createAnalyser();
              analyser.fftSize = 256;
              const remoteStream = new MediaStream([ev.track]);
              const source = ctx.createMediaStreamSource(remoteStream);
              source.connect(analyser);
              remoteAnalyserRef.current = analyser;

              const mixCtx = mixCtxRef.current;
              const mixDestination = mixDestinationRef.current;
              if (mixCtx && mixDestination && !remoteMixConnectedRef.current) {
                remoteMixConnectedRef.current = true;
                mixCtx.createMediaStreamSource(remoteStream).connect(mixDestination);
              }

              // Poll AI speaker level for orb animation (mutate ref — no React setState)
              const aiLevelBuf = new Uint8Array(analyser.frequencyBinCount);
              const pollAiLevel = () => {
                analyser.getByteFrequencyData(aiLevelBuf);
                let sum = 0;
                for (let i = 0; i < aiLevelBuf.length; i++) sum += aiLevelBuf[i];
                const avg = sum / aiLevelBuf.length / 128;
                liveLevelsRef.current.ai = Math.min(avg * 2.5, 1);
                aiLevelRafRef.current = requestAnimationFrame(pollAiLevel);
              };
              aiLevelRafRef.current = requestAnimationFrame(pollAiLevel);
            });
            return pc;
          },
        });

        // 6. Create session — SDK sends session.update with full config on connect
        const realtimeSession = new RealtimeSession(createInterviewAgent(interviewConfig), {
          model: 'gpt-realtime-2',
          transport,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          config: {
            audio: {
              input: {
                transcription: { model: transcriptionModel, language: 'en' },
                turnDetection: {
                  type: 'semantic_vad',
                  interruptResponse: false,
                  createResponse: false,
                  eagerness: 'high',
                },
                noiseReduction: { type: noiseReductionType },
              },
              output: { voice },
            },
          } as Partial<RealtimeSessionConfig>,
        });
        sessionRef.current = realtimeSession;

        // 7. Wire raw event handler before connecting
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (realtimeSession.transport as any).on('*', handleRawEvent);

        // 8. Connect — resolves after session.updated is acknowledged by server
        await realtimeSession.connect({ apiKey: ephemeralKey });

        // 8b. GUARANTEE interrupt_response:false before the AI's first audio (the
        //     server DEFAULTS it to true). Belt-and-suspenders with the muted-while-
        //     AI-speaks design — we never want a late reply to truncate the AI. We
        //     push the turn-detection config explicitly, raw (snake_case) — the same
        //     shape the opening→interviewing transition uses and that the server
        //     honors — rather than relying solely on the SDK's camelCase initial config.
        realtimeSession.transport.sendEvent({
          type: 'session.update',
          session: { type: 'realtime', audio: { input: { turn_detection: OPENING_VAD_CONFIG } } },
        });

        // 9. Session is ready; trigger the AI's opening message
        setStatus('ready');
        setOrbState('thinking');
        sendResponseCreate('start.initial_opening');
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setStatus('error');
        setOrbState('idle');
        stopResources();
      }
    },
    [handleRawEvent, stopResources, applyMicOpen, sendMixedAudioChunk, sendResponseCreate, clearPlaybackWatchers],
  );

  // ── stop() ───────────────────────────────────────────────────────────────────

  const stop = useCallback(() => {
    persistTranscript();
    stopRecorder(mixedRecorderRef.current);
    stopUserTurnCapture();
    mixedRecorderRef.current = null;
    const session = sessionRef.current;
    sessionRef.current = null;
    session?.close();
    stopResources();
    partialRef.current = {};
    pendingUserPlaceholderQueueRef.current = [];
    pendingUserPlaceholderByItemIdRef.current = {};
    pendingUserSpeechStartTimesRef.current = [];
    pendingAiEntriesRef.current = [];
    userTurnPendingItemIdRef.current = null;
    userTurnPendingBlobRef.current = null;
    userTurnDiscardRef.current = false;
    pendingAudioFileByItemIdRef.current = {};
    remoteMixConnectedRef.current = false;
    interviewFinalizedRef.current = false;
    deferFinalizeRef.current = false;
    responseHasAudioRef.current = false;
    closingPlaybackSeenRef.current = false;
    awaitingClosingFollowUpRef.current = false;
    suppressClosingFollowUpRef.current = false;
    openingAssistantItemSeenRef.current = false;
    currentResponseIdRef.current = null;
    if (speechTimerRef.current) { clearTimeout(speechTimerRef.current); speechTimerRef.current = null; }
    userSpeakingRef.current = false;
    phaseRef.current = 'opening';
    setStatus('idle');
    setOrbState('idle');
    setEnded(false);
    setPhase('opening');
    setError(null);
    setTranscript([]);
    transcriptRef.current = [];
    setCurrentAiText('');
    setAiTurnId('');
  }, [stopResources, persistTranscript, stopUserTurnCapture]);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const sendText = useCallback((text: string) => {
    const session = sessionRef.current;
    if (!session || userPausedRef.current) return;

    // If AI audio is still playing, stop watchers and flush what was said
    if (isAiRespondingRef.current) {
      // Only cancel the API response if it's still active — after response.done
      // the response is closed and cancelling would return an error
      if (isResponseActiveRef.current) {
        safeCancelResponse('sendText.interrupt');
      }
      clearPlaybackWatchers();
      isAiRespondingRef.current = false;
      flushPendingAiEntries();
    }

    const id = `txt-${Date.now()}`;
    const turnPhase = phaseRef.current;
    const nextWithText: TranscriptEntry[] = [
      ...transcriptRef.current,
      {
        id,
        role: 'user',
        text,
        phase: turnPhase,
        timestamp: new Date().toISOString(),
      },
    ];
    transcriptRef.current = nextWithText;
    setTranscript(nextWithText);
    persistTranscript();
    if (turnPhase === 'opening') {
      phaseRef.current = 'interviewing';
      setPhase('interviewing');
      session.transport.sendEvent({
        type: 'session.update',
        session: { type: 'realtime', audio: { input: { turn_detection: INTERVIEWING_VAD_CONFIG } } },
      });
    }
    session.transport.sendEvent({
      type: 'conversation.item.create',
      item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
    });
    sendResponseCreate('sendText');
    setOrbState('thinking');
  }, [clearPlaybackWatchers, flushPendingAiEntries, persistTranscript, safeCancelResponse, sendResponseCreate]);

  const repeat = useCallback(() => {
    const session = sessionRef.current;
    if (!session || userPausedRef.current) return;
    session.transport.sendEvent({ type: 'input_audio_buffer.clear' });
    session.transport.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text: 'Could you please repeat your last question?' }],
      },
    });
    sendResponseCreate('repeat');
    setOrbState('thinking');
  }, [sendResponseCreate]);

  const mute = useCallback(
    (shouldMute: boolean) => {
      userMutedRef.current = shouldMute;
      if (shouldMute) {
        applyMicOpen(false);
      } else if (!userPausedRef.current && !isAiRespondingRef.current) {
        applyMicOpen(true);
      }
      updateRecordingCapture();
    },
    [applyMicOpen, updateRecordingCapture],
  );

  const pause = useCallback((shouldPause: boolean) => {
    userPausedRef.current = shouldPause;
    if (shouldPause) {
      clearPendingTurnBarriers();
      userSpeakingRef.current = false;
      safeCancelResponse('pause.user');
      isAiRespondingRef.current = false;
      clearPlaybackWatchers();
      applyMicOpen(false);
      updateRecordingCapture();
    } else {
      updateRecordingCapture();
      if (!userMutedRef.current && !isAiRespondingRef.current) {
        applyMicOpen(true);
        setOrbState('listening');
      }
    }
    sessionRef.current?.transport.sendEvent({
      type: 'session.update',
      session: {
        type: 'realtime',
        audio: {
          input: {
            turn_detection: shouldPause
              ? null
              : (phaseRef.current === 'opening' ? OPENING_VAD_CONFIG : INTERVIEWING_VAD_CONFIG),
          },
        },
      },
    });
  }, [applyMicOpen, clearPendingTurnBarriers, clearPlaybackWatchers, safeCancelResponse, updateRecordingCapture]);

  return {
    status,
    orbState,
    liveLevelsRef,
    ended,
    phase,
    error,
    transcript,
    currentAiText,
    streamingAiText,
    aiTurnId,
    start,
    stop,
    sendText,
    repeat,
    mute,
    pause,
  };
}
